'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import { useFormMethods } from '@sola/ark-core/molecules/form/form.tsx';
import {
  Sheet,
  SheetActions,
  SheetBody,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTrigger,
} from '@sola/ark-core/molecules/sheet/sheet.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@sola/ark-core/molecules/tooltip/tooltip.tsx';
import type { ContactDoNotUpdate } from '@sola/schema/dotcom/contacts/update.ts';

import { ContactPreferenceOptOutAlert } from '../../molecules/contact-preference-opt-out-alert/contact-preference-opt-out-alert.tsx';
import { ContactPreferencesForm } from '../../molecules/contact-preferences-form/contact-preferences-form.tsx';
import { SettingsErrorAlertMessage } from '../../utils/alerts.tsx';
import {
  ContactPreferenceType,
  type ContactPreferencesFormValues,
  buildPreferencePayload,
  buildReactivationUpdates,
  useContactPreferencesSheet,
} from './contact-preferences-sheet.logic.ts';

/**
 * Save button for the preferences sheet. Its submit button is associated with
 * the form by ID because the footer is intentionally outside the form.
 */
interface ContactPreferencesSaveButtonProps {
  formId: string;
  isDirty: boolean;
  isSaving: boolean;
  forceDisabled: boolean;
  children: React.ReactNode;
}

const ContactPreferencesSaveButton: React.FC<
  ContactPreferencesSaveButtonProps
> = ({ formId, isDirty, isSaving, forceDisabled, children }) => {
  const isDisabled = forceDisabled || !isDirty || isSaving;

  return (
    <ButtonStyle fullWidth isBusy={isSaving} disabled={isDisabled}>
      <button form={formId} type="submit">
        {children}
      </button>
    </ButtonStyle>
  );
};

export interface ContactPreferencesSheetProps {
  contactId: string;
}

export const ContactPreferencesSheet: React.FC<
  ContactPreferencesSheetProps
> = ({ contactId }) => {
  const { t } = useTranslation(['my-settings', 'core', 'preferences']);
  const formMethods = useFormMethods();
  const [isDirty, setIsDirty] = useState(false);

  const {
    isOpen,
    setIsOpen,
    formDefaultValues,
    currentPreferences,
    optOutPreferenceTypes,
    supporterName,
    isSaving,
    submitPreferenceUpdate,
  } = useContactPreferencesSheet({ contactId });

  const formId = `contact-preferences-form-${contactId}`;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setIsDirty(false);
    }
  };

  const handleReactivate = async (
    preferences: ContactDoNotUpdate['preferences'],
  ) => {
    formMethods.current?.clearErrors();
    const didSucceed = await submitPreferenceUpdate({
      contactId,
      preferences,
      name: supporterName,
    });

    if (didSucceed) {
      const { fieldUpdates } = buildReactivationUpdates({
        currentPreferences,
        change: preferences,
      });
      for (const { path, value } of fieldUpdates) {
        formMethods.current?.setValue(path, value);
      }
      return;
    }

    formMethods.current?.setGeneralError(
      {
        message: (
          <SettingsErrorAlertMessage i18nKey="preferences:contactPreferenceUpdate.error" />
        ),
      },
      { scrollIntoView: true, scrollPosition: 'center' },
    );
  };

  const handleSave = async (values: ContactPreferencesFormValues) => {
    formMethods.current?.clearErrors();
    const preferences = buildPreferencePayload(
      currentPreferences,
      values,
      optOutPreferenceTypes,
    );

    if (Object.keys(preferences).length === 0) {
      setIsDirty(false);
      setIsOpen(false);
      return;
    }

    const didSucceed = await submitPreferenceUpdate({
      contactId,
      preferences,
      name: supporterName,
    });

    if (didSucceed) {
      setIsDirty(false);
      setIsOpen(false);
      return;
    }

    formMethods.current?.setGeneralError(
      {
        message: (
          <SettingsErrorAlertMessage i18nKey="preferences:contactPreferenceUpdate.error" />
        ),
      },
      { scrollIntoView: true, scrollPosition: 'center' },
    );
  };

  const bothChannelsOptedOut =
    optOutPreferenceTypes.has(ContactPreferenceType.Email) &&
    optOutPreferenceTypes.has(ContactPreferenceType.Text);

  const title = supporterName
    ? t('preferences:title', { context: 'name', name: supporterName })
    : t('preferences:title');

  return (
    <Sheet open={isOpen} onOpenChange={handleOpenChange} isBusy={isSaving}>
      {currentPreferences?.doNotContact ? (
        <Tooltip>
          <TooltipTrigger>
            <LinkStyle
              noUnderline
              noPadding
              size="sm"
              inline
              purpose="disabled"
            >
              <button type="button" aria-disabled="true">
                {t('my-settings:contactCard.changePreferences')}
              </button>
            </LinkStyle>
          </TooltipTrigger>
          <TooltipContent align="center" size="sm" usePortal>
            <p>{t('preferences:doNotContactMessage')}</p>
          </TooltipContent>
        </Tooltip>
      ) : (
        <SheetTrigger>
          <LinkStyle noUnderline noPadding size="md" inline>
            <button type="button">
              {t('my-settings:contactCard.changePreferences')}
            </button>
          </LinkStyle>
        </SheetTrigger>
      )}
      <SheetContent title={title}>
        <SheetHeader>{title}</SheetHeader>
        <SheetBody>
          {currentPreferences?.doNotEmail && (
            <ContactPreferenceOptOutAlert
              channel={ContactPreferenceType.Email}
              className="mb-comfy-md"
              isBusy={isSaving}
              onReactivate={handleReactivate}
            />
          )}
          {currentPreferences?.doNotText && (
            <ContactPreferenceOptOutAlert
              channel={ContactPreferenceType.Text}
              className="mb-comfy-md"
              isBusy={isSaving}
              onReactivate={handleReactivate}
            />
          )}
          <ContactPreferencesForm
            id={formId}
            aria-label={title}
            formMethods={formMethods}
            onSubmit={handleSave}
            onDirtyChange={setIsDirty}
            defaultValues={formDefaultValues}
            optOutChannels={optOutPreferenceTypes}
            preferenceRowClassName="shrink-0 pr-roomy-xs"
          />
        </SheetBody>
        <SheetFooter>
          <SheetActions>
            <SheetClose asChild>
              <ButtonStyle
                size="lg"
                purpose="neutral"
                variant="secondary"
                fullWidth
              >
                <button type="button">{t('core:actions.cancel')}</button>
              </ButtonStyle>
            </SheetClose>

            <ContactPreferencesSaveButton
              formId={formId}
              isDirty={isDirty}
              isSaving={isSaving}
              forceDisabled={bothChannelsOptedOut}
            >
              {t('core:actions.save')}
            </ContactPreferencesSaveButton>
          </SheetActions>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
